import { Fragment } from "react";
import { fieldDisplay, isBlank, plural } from "@/components/checkins/public/diary/diaryUtils";
import { DiaryTrendChart, fieldLabel as short } from "./DiaryTrendChart";

const NUMERIC = ["rating", "integer"];

function dayLabel(start, n) {
  if (!start) return `Day ${n}`;
  const d = new Date(`${start}T00:00:00`);
  d.setDate(d.getDate() + n - 1);
  return `Day ${n} · ${d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}`;
}

const Stat = ({ label, value, sub, testId }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-2.5" data-testid={testId}>
    <div className="text-2xs font-semibold uppercase tracking-[0.12em] text-zinc-500">{label}</div>
    <div className="font-mono-num text-lg font-semibold text-zinc-950">{value}</div>
    {sub && <div className="text-xs text-zinc-600">{sub}</div>}
  </div>
);

function Stats({ config, summary }) {
  const fields = [...config.entry_fields, ...config.followup_fields];
  return (
    <div className="grid gap-2 sm:grid-cols-3" data-testid="diary-stats">
      <Stat label="Logged" value={plural(summary.entries, config.entry_label)} sub={`on ${summary.days_logged} of ${summary.days_total} days`} testId="diary-stat-logged" />
      {fields.filter((f) => NUMERIC.includes(f.type)).map((f) => {
        const s = summary.fields[f.code] || {};
        return <Stat key={f.code} label={short(f)} value={s.mean == null ? "—" : `${s.mean}${f.unit === "minutes" ? " min" : ""}`} sub={`mean · max ${s.max ?? "—"} · ${s.missing} blank`} testId={`diary-stat-${f.code}`} />;
      })}
      {fields.filter((f) => f.type === "single").map((f) => {
        const s = summary.fields[f.code] || {};
        const parts = f.options.map((o) => `${o.label} ${s.counts?.[o.value] ?? 0}`);
        return <Stat key={f.code} label={short(f)} value={`${s.answered ?? 0} answered`} sub={`${parts.join(" · ")} · ${s.missing ?? 0} blank`} testId={`diary-stat-${f.code}`} />;
      })}
    </div>
  );
}

function DayRows({ n, start, day, cols, noteFields }) {
  const notes = noteFields.filter((f) => !isBlank(day.notes?.[f.code]));
  const label = dayLabel(start, n);
  const span = day.entries.length + (notes.length ? 1 : 0) || 1;
  const head = <td rowSpan={span} className="whitespace-nowrap px-2 py-1.5 align-top font-medium text-zinc-800">{label}</td>;
  if (!day.entries.length && !notes.length) {
    return <tr className="border-t border-zinc-100" data-testid={`diary-row-day-${n}`}>{head}<td colSpan={cols.length} className="px-2 py-1.5 text-zinc-400">Nothing logged</td></tr>;
  }
  const cell = (f, v) => <td key={f.code} className={`px-2 py-1.5 ${f.type === "rating" && v >= 8 ? "font-semibold text-red-700" : "text-zinc-900"}`}>{fieldDisplay(f, v)}</td>;
  return (
    <Fragment>
      {day.entries.map((e, i) => <tr key={e.id} className="border-t border-zinc-100" data-testid={`diary-row-day-${n}-${i}`}>{i === 0 && head}{cols.map((f) => cell(f, e[f.code]))}</tr>)}
      {notes.length > 0 && (
        <tr className={day.entries.length ? "" : "border-t border-zinc-100"}>
          {!day.entries.length && head}
          <td colSpan={cols.length} className="bg-zinc-50 px-2 py-1.5 text-xs text-zinc-700">
            {notes.map((f) => <span key={f.code} className="mr-4"><b>{short(f)}:</b> {day.notes[f.code]}</span>)}
          </td>
        </tr>
      )}
    </Fragment>
  );
}

/** Staff read-out of a diary: stats (blank ≠ 0), flags and every logged entry by day. */
export function DiaryReport({ config, diary, summary, flags, start }) {
  const cols = [...config.entry_fields, ...config.followup_fields];
  return (
    <div className="space-y-3" data-testid="diary-report">
      <Stats config={config} summary={summary} />
      <DiaryTrendChart config={config} diary={diary} />
      {flags?.length ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" data-testid="diary-flags">
          <b>Flags</b><ul className="mt-1 list-disc pl-5">{flags.map((f) => <li key={f.code}>{f.label}</li>)}</ul>
        </div>
      ) : <p className="text-sm text-zinc-600" data-testid="diary-no-flags">No flag rules met.</p>}
      <div className="overflow-x-auto rounded-lg border border-zinc-200">
        <table className="w-full text-sm" data-testid="diary-day-table">
          <thead className="bg-zinc-50 text-left text-2xs uppercase tracking-wide text-zinc-500">
            <tr><th className="px-2 py-1.5">Day</th>{cols.map((f) => <th key={f.code} className="px-2 py-1.5">{short(f)}</th>)}</tr>
          </thead>
          <tbody>
            {Array.from({ length: config.days }, (_, i) => i + 1).map((n) => (
              <DayRows key={n} n={n} start={start} day={diary?.days?.[String(n)] || { entries: [], notes: {} }} cols={cols} noteFields={config.day_fields} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
